import React, { useEffect, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useDropdown } from '../../../contexts/DropdownContext';
import { useTranslation } from 'react-i18next';
import { PiCheckCircleLight } from 'react-icons/pi';
import Flag from 'react-flagkit';
import ProfileImage from '../../Header/ProfileImage';
import debounce from 'lodash/debounce';
import { useInView } from 'react-intersection-observer';
import Skeleton from './Skeleton';
import { createPortal } from "react-dom";
import { motion } from 'framer-motion';
import { Tooltip } from '@mui/material';
import { validateChoice } from '../../../utils/validation/validators';
import { escapeHtml } from '../../../utils/validation/sanitizers';
import FieldShell, { fieldSurface, FOCUS_RING_ONLY, FIELD_VALUE_CLASS } from './FieldShell';
import { cn } from './Input';
import { normalise } from '../access/accessModel';

export interface DropdownOption {
  value: string;
  label: string;
  active?: boolean;
  flag?: string;
  icon?: React.ReactNode;
  id?: string | number;
  parent_id?: string | number | null;
  children?: DropdownOption[];
  sort_order?: number;
  avatarUrl?: string | null;
  email?: string;
}

interface DropdownProps {
  id?: string;
  label?: string;
  options: DropdownOption[];
  multiple?: boolean;
  placeholder?: string;
  onChange?: (selectedValues: string | string[]) => void;
  onSelect?: (selectedOption: DropdownOption) => void;
  value?: string | string[];
  className?: string;
  error?: string;
  description?: string;
  /** Muted line under the control - FieldShell's help row. */
  helpText?: string;
  italicTxt?: string;
  showFlags?: boolean;
  useApi?: boolean;
  fetchOptions?: (query: string, page: number) => Promise<DropdownOption[]>;
  totalPages?: number;
  renderOption?: (option: DropdownOption) => React.ReactNode;
  compact?: boolean;
  leftIcon?: React.ReactNode;
  infoText?: string;
  infoTrigger?: 'hover' | 'click';
  hierarchical?: boolean;
  width?: string;
  fullWidth?: boolean;
  thinner?: boolean;
  required?: boolean;
  useLoadMoreButton?: boolean;
  popupMinWidth?: number;
  popupMaxHeight?: number;
  disableParentSelection?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  id,
  label,
  options: initialOptions,
  multiple = false,
  placeholder,
  onChange,
  value,
  className,
  onSelect,
  error,
  description,
  helpText,
  italicTxt,
  showFlags,
  useApi = false,
  fetchOptions,
  totalPages,
  renderOption,
  compact = false,
  leftIcon,
  infoText,
  infoTrigger = 'hover',
  hierarchical = false,
  width = 'w-48',
  fullWidth = false,
  thinner = false,
  required = false,
  useLoadMoreButton = false,
  popupMinWidth,
  popupMaxHeight,
  disableParentSelection = false,
}) => {
  const { openDropdownId, setOpenDropdownId } = useDropdown();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState<DropdownOption[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [shouldDropUp, setShouldDropUp] = useState(false);
  const dropdownListRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const generatedId = React.useId();
  const uniqueId = id || generatedId;
  const hasInfo = Boolean(infoText);
  const [openInfo, setOpenInfo] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string | number>>(new Set());
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0 });

  const { ref: loadMoreRef, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    const normalizedOptions = initialOptions.map((opt) => ({
      ...opt,
      value: opt.value || String(opt.id),
    }));
    const processedOptions = hierarchical ? buildHierarchy(normalizedOptions) : normalizedOptions;
    setOptions(processedOptions);
  }, [initialOptions, hierarchical]);

  const buildHierarchy = (items: DropdownOption[]): DropdownOption[] => {
    const itemMap = new Map<string | number, DropdownOption>();
    const roots: DropdownOption[] = [];

    items.forEach((item) => {
      itemMap.set(item.id || item.value, item);
    });

    itemMap.forEach((item) => {
      if (item.parent_id === null || item.parent_id === undefined) {
        roots.push(item);
      } else {
        const parent = itemMap.get(item.parent_id);
        if (parent) {
          if (!parent.children) parent.children = [];
          parent.children.push(item);
        }
      }
    });

    return roots.sort((a, b) => {
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  };

  useEffect(() => {
    if (value) {
      setSelectedValues(Array.isArray(value) ? value : [value]);
    } else {
      setSelectedValues([]);
    }
  }, [value]);

  const updateMenuPosition = useCallback(() => {
    if (dropdownRef.current && dropdownListRef.current && isOpen) {
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const dropdownListHeight = dropdownListRef.current.offsetHeight;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - dropdownRect.bottom;

      const shouldFlipUp = spaceBelow < dropdownListHeight && dropdownRect.top > dropdownListHeight;
      setShouldDropUp(shouldFlipUp);

      const popupWidth = popupMinWidth ? Math.max(dropdownRect.width, popupMinWidth) : dropdownRect.width;
      setMenuPosition({
        top: shouldFlipUp 
          ? dropdownRect.top - dropdownListHeight 
          : dropdownRect.bottom,
        left: dropdownRect.left,
        width: popupWidth,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      updateMenuPosition();
      const handleScroll = () => updateMenuPosition();
      const handleResize = () => updateMenuPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (openDropdownId !== uniqueId && isOpen) {
      setIsOpen(false);
    }
  }, [openDropdownId, uniqueId, isOpen]);

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
      setOpenDropdownId(null);
      setSearchQuery('');
      setExpandedParents(new Set());
    } else {
      setIsOpen(true);
      setOpenDropdownId(uniqueId);
      if (useApi && fetchOptions) {
        loadInitialOptions();
      }
    }
  };

  const toggleParentExpanded = (parentId: string | number) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parentId)) {
      newExpanded.delete(parentId);
    } else {
      newExpanded.add(parentId);
    }
    setExpandedParents(newExpanded);
  };

  const isValidOption = (optionValue: string, optionsToCheck: DropdownOption[]): boolean => {
    if (!hierarchical) {
      return optionsToCheck.some((opt) => opt.value === optionValue);
    }
    const allOpts = flattenHierarchy(optionsToCheck);
    return allOpts.some((opt) => opt.value === optionValue);
  };

  /** Recursively flatten all options in a hierarchy tree */
  const flattenHierarchy = (items: DropdownOption[]): DropdownOption[] => {
    const result: DropdownOption[] = [];
    const traverse = (nodes: DropdownOption[]) => {
      for (const node of nodes) {
        result.push(node);
        if (node.children && node.children.length > 0) {
          traverse(node.children);
        }
      }
    };
    traverse(items);
    return result;
  };

  const handleSelect = (optionValue: string) => {
    const optionsToCheck = hierarchical ? options : options;
    const allOptions = hierarchical 
      ? flattenHierarchy(options)
      : options;
    const option = allOptions.find((opt) => opt.value === optionValue);

    if (!option || option?.active === false) {
      console.warn(`Invalid option selected: ${optionValue}`);
      return;
    }

    const sanitizedValue = String(optionValue).trim();

    if (!isValidOption(sanitizedValue, optionsToCheck)) {
      console.warn(`Option not in allowed choices: ${sanitizedValue}`);
      return;
    }

    let newSelected: string[] = [];
    if (multiple) {
      if (selectedValues.includes(sanitizedValue)) {
        newSelected = selectedValues.filter((v) => v !== sanitizedValue);
      } else {
        newSelected = [...selectedValues, sanitizedValue];
      }
    } else {
      newSelected = [sanitizedValue];
      setIsOpen(false);
      setOpenDropdownId(null);
      setSearchQuery('');
      setExpandedParents(new Set());
    }
    setSelectedValues(newSelected);
    if (onChange) {
      onChange(multiple ? newSelected : newSelected[0]);
    }

    if (option && onSelect) {
      onSelect(option);
    }
  };

  const loadInitialOptions = useCallback(async () => {
    if (!useApi || !fetchOptions) return;
    setIsLoading(true);
    const newOptions = await fetchOptions('', 1);
    setPage(1);
    setHasMore(totalPages ? 1 < totalPages : newOptions.length >= 12);
    setIsLoading(false);
  }, [useApi, fetchOptions, totalPages]);

  const loadMoreOptions = useCallback(async () => {
    if (!useApi || !fetchOptions || !hasMore || isLoading) return;
    setIsLoading(true);
    const nextPage = page + 1;
    const newOptions = await fetchOptions(searchQuery, nextPage);
    setPage(nextPage);
    if (totalPages && nextPage >= totalPages) {
      setHasMore(false);
    } else {
      setHasMore(newOptions.length >= 12);
    }
    setIsLoading(false);
  }, [useApi, fetchOptions, hasMore, isLoading, page, searchQuery, totalPages]);

  useEffect(() => {
    if (inView && hasMore && !isLoading && !useLoadMoreButton) {
      loadMoreOptions();
    }
  }, [inView, hasMore, isLoading, useLoadMoreButton]);

  const debouncedSearch = useCallback(
    debounce(async (query: string) => {
      if (!useApi || !fetchOptions) return;
      setIsLoading(true);
      const newOptions = await fetchOptions(query, 1);
      setOptions(newOptions);
      setPage(1);
      setHasMore(totalPages ? 1 < totalPages : newOptions.length >= 12);
      setIsLoading(false);
    }, 300),
    [useApi, fetchOptions, totalPages]
  );

  useEffect(() => {
    if (useApi && isOpen) {
      debouncedSearch(searchQuery);
    }
  }, [searchQuery, useApi, isOpen]);

  const firstValue = selectedValues[0];
  const allOptions = hierarchical 
    ? flattenHierarchy(options)
    : options;
  const selectedOption = allOptions.find((opt) => opt.value === firstValue);
  const selectedLabels = multiple
    ? selectedValues.map((val) => allOptions.find((opt) => opt.value === val)?.label).filter(Boolean)
    : [];
  const displayValue = multiple
    ? selectedLabels.length > 1
      ? `${selectedLabels[0]} +${selectedLabels.length - 1} more`
      : selectedLabels[0] || ''
    : firstValue != null
      ? selectedOption?.label || ''
      : '';
  const multiSelectTooltip = multiple && selectedLabels.length > 1
    ? selectedLabels.join(', ')
    : '';
  const selectedFlag = showFlags && firstValue ? selectedOption?.flag : null;
  const selectedIcon = selectedOption?.icon;
  const selectedAvatar = selectedOption?.avatarUrl;
  const selectedEmail = selectedOption?.email;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const triggerEl = dropdownRef.current;
      const menuEl = dropdownListRef.current;

      if (
        triggerEl &&
        !triggerEl.contains(target) &&
        menuEl &&
        !menuEl.contains(target)
      ) {
        setIsOpen(false);
        setOpenDropdownId(null);
        setSearchQuery('');
        setExpandedParents(new Set());
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredOptions = useApi
    ? options
    : options.filter((option) => {
      const q = searchQuery.toLowerCase();
      if (!hierarchical) {
        return option.label && option.label.toString().toLowerCase().includes(q);
      }
      // For hierarchical, show parent if it or any descendant matches
      const hasMatchingDescendant = (node: DropdownOption): boolean => {
        if (node.label?.toString().toLowerCase().includes(q)) return true;
        return node.children?.some(child => hasMatchingDescendant(child)) || false;
      };
      return hasMatchingDescendant(option);
    });

  const toggleClick = () => {
    if (infoTrigger === 'click') setOpenInfo((o) => !o);
  };
  const openHover = () => {
    if (infoTrigger === 'hover') setOpenInfo(true);
  };
  const closeHover = () => {
    if (infoTrigger === 'hover') setOpenInfo(false);
  };

  /*
    While searching, a group whose entries match opens by itself and shows just
    those entries - otherwise "Dismissal" found only a closed "Employment".
    A group that matches by its own name keeps all its entries.
  */
  const searching = searchQuery.trim().length > 0;
  const matchesSearch = (node: DropdownOption): boolean => {
    const q = searchQuery.toLowerCase();
    if (node.label?.toString().toLowerCase().includes(q)) return true;
    return node.children?.some(matchesSearch) || false;
  };

  const renderHierarchicalOptions = (items: DropdownOption[], depth: number = 0) => {
    return items.flatMap((parentOption) => {
      const ownMatch = searching && !!parentOption.label?.toString().toLowerCase().includes(searchQuery.toLowerCase());
      const visibleChildren = searching && !ownMatch
        ? (parentOption.children || []).filter(matchesSearch)
        : (parentOption.children || []);
      const hasChildren = visibleChildren.length > 0;
      const isExpanded = expandedParents.has(parentOption.id || parentOption.value) || (searching && hasChildren);
      const paddingLeft = depth * 16 + 16; // 16px base + 16px per depth level

      const parentItems = (
        <li key={`parent-${parentOption.id || parentOption.value}`}>
          <div
            className="cursor-pointer select-none relative py-2 pe-4 text-fg-body hover:bg-surface-soft flex items-center justify-between group transition-colors"
            style={{ paddingLeft: `${paddingLeft}px` }}
            role="option"
            aria-selected={value === parentOption.value}
            tabIndex={0}
          >
            <div className="flex-1 flex items-center">
              {hasChildren ? (
                <button
                  type="button"
                  className="flex items-center justify-center w-5 h-5 me-1 rounded hover:bg-surface-sunken flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleParentExpanded(parentOption.id || parentOption.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleParentExpanded(parentOption.id || parentOption.value);
                    }
                  }}
                  tabIndex={0}
                  aria-label={isExpanded ? t('Collapse') : t('Expand')}
                >
                  <svg
                    className={`w-3.5 h-3.5 transition-transform text-fg-muted ${
                      isExpanded ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <span className="inline-block me-1 w-5"></span>
              )}
              <span
                className={clsx(
                  'flex-1 text-xs flex items-center gap-1 py-0.5',
                  disableParentSelection && hasChildren
                    ? 'text-fg-muted font-medium cursor-default'
                    : 'text-fg-body cursor-pointer'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  if (disableParentSelection && hasChildren) {
                    toggleParentExpanded(parentOption.id || parentOption.value);
                    return;
                  }
                  handleSelect(parentOption.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    if (disableParentSelection && hasChildren) {
                      toggleParentExpanded(parentOption.id || parentOption.value);
                      return;
                    }
                    handleSelect(parentOption.value);
                  }
                }}
              >
                {selectedValues.includes(parentOption.value) && !disableParentSelection && <PiCheckCircleLight className='w-4 h-4 text-champagne' />}
                <span>{t(parentOption.label)}</span>
              </span>
            </div>
          </div>

          {hasChildren && isExpanded && (
            <div>
              {renderHierarchicalOptions(visibleChildren, depth + 1)}
            </div>
          )}
        </li>
      );

      return parentItems;
    });
  };

  const renderFlatOptions = (items: DropdownOption[]) => {
    return items.map((option) => {
      const optionContent = renderOption ? (
        renderOption(option)
      ) : (
        <div className="flex items-center w-full gap-3">
          {option.avatarUrl || option.email ? (
            <ProfileImage
              profilePicture={normalise(option).avatar}
              email={option.email || ''}
              initials={option.label?.charAt(0).toUpperCase() || 'U'}
              size="h-6 w-6"
              className="flex-shrink-0"
            />
          ) : showFlags && option.flag ? (
            <Flag country={option.flag} size={16} className="me-2" />
          ) : option.icon ? (
            typeof option.icon === 'string' ? (
              <img src={option.icon} alt={option.label} className="w-5 h-5" />
            ) : (
              <span>{option.icon}</span>
            )
          ) : null}
          <div className="flex-1 min-w-0">
            <span className="text-xs truncate">{t(option.label)}</span>
            {option.email && (
              <p className="text-xs text-fg-muted truncate">{option.email}</p>
            )}
          </div>
        </div>
      );

      return (
        <li
          key={option.value}
          className={clsx(
            'cursor-pointer select-none relative py-2 ps-4 pe-4 w-full',
            selectedValues.includes(option.value) ? 'bg-surface-soft' : '',
            option.active === false
              ? 'text-fg-muted cursor-not-allowed'
              : 'hover:bg-surface-soft text-fg-body transition-colors'
          )}
          onClick={() => handleSelect(option.value)}
          role="option"
          aria-selected={selectedValues.includes(option.value)}
          aria-disabled={option.active === false}
          tabIndex={option.active === false ? -1 : 0}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && option.active !== false) {
              handleSelect(option.value);
            }
          }}
        >
          {multiple && (
            <span className="absolute inset-y-0 start-0 flex items-center ps-3">
              <input
                type="checkbox"
                checked={selectedValues.includes(option.value)}
                readOnly
                className="h-4 w-4 rounded accent-champagne border-hairline transition duration-150 ease-in-out me-2"
              />
            </span>
          )}
          <div className={multiple ? 'ms-6' : ''}>{optionContent}</div>
        </li>
      );
    });
  };

  const optionsToDisplay = hierarchical ? filteredOptions : filteredOptions;

  const infoButton = hasInfo ? (
    <div
      className="relative inline-flex items-center"
      onMouseEnter={openHover}
      onMouseLeave={closeHover}
    >
      <button
        type="button"
        onClick={toggleClick}
        aria-label={t('Info')}
        className={clsx(
          'ms-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-hairline text-[10px] leading-none',
          'text-fg-muted hover:bg-surface-soft transition-colors',
          FOCUS_RING_ONLY
        )}
      >
        i
      </button>

      {openInfo && (
        <div
          className="absolute z-50 mt-2 w-64 rounded-md glass-menu p-3 text-xs text-fg-body shadow-lg"
          role="tooltip"
        >
          {t(infoText!)}
        </div>
      )}
    </div>
  ) : null;

  return (
    <FieldShell
      id={uniqueId}
      label={label}
      required={required}
      description={description}
      italicText={italicTxt}
      error={error}
      helpText={helpText}
      labelSuffix={infoButton}
      className="relative"
    >
      <div ref={dropdownRef} className="relative">
      <div
        id={uniqueId}
        // cn(), not clsx(): fieldSurface() ships `w-full`, and the `width` prop
        // ("w-48") has to be able to beat it. twMerge resolves that by last-wins;
        // plain concatenation would leave it to Tailwind's output order.
        className={cn(
          fieldSurface({ error: !!error, height: false }),
          'cursor-pointer flex justify-between items-center',
          thinner ? 'px-3 py-0.5' : compact ? 'px-3 py-1' : 'px-3 py-1.5',
          // `thinner`/`compact` are explicit density requests, so only the default
          // density normalises to the shared h-9.
          !thinner && !compact && 'h-9',
          fullWidth ? 'w-full' : width,
          className
        )}
        onClick={toggleDropdown}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            toggleDropdown();
          }
        }}
      >
        <div className="flex items-center gap-1">
          {leftIcon && <span>{leftIcon}</span>}
          {selectedAvatar || selectedEmail ? (
            <ProfileImage
              profilePicture={selectedAvatar || null}
              email={selectedEmail || ''}
              initials={selectedOption?.label?.charAt(0).toUpperCase() || 'U'}
              size="h-5 w-5"
              className="flex-shrink-0"
            />
          ) : showFlags && selectedFlag ? (
            <Flag country={selectedFlag} size={16} className="me-2" />
          ) : selectedIcon ? (
            <span className="me-2">{selectedIcon}</span>
          ) : null}
          {multiSelectTooltip ? (
            <Tooltip title={multiSelectTooltip} arrow placement="top">
              <span className={clsx('truncate', displayValue ? FIELD_VALUE_CLASS : 'text-sm text-fg-muted/60')}>
                {t(displayValue) || (placeholder ? t(placeholder) : t('Select'))}
              </span>
            </Tooltip>
          ) : (
            <span className={clsx('truncate', displayValue ? FIELD_VALUE_CLASS : 'text-sm text-fg-muted/60')}>
              {t(displayValue) || (placeholder ? t(placeholder) : t('Select'))}
            </span>
          )}
        </div>
        <svg
          className={clsx(
            'text-fg-muted flex-shrink-0',
            thinner ? 'w-4 h-4' : compact ? 'w-4 h-4' : 'w-5 h-5'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {isOpen &&
        createPortal(
          <motion.div
            initial={{ opacity: 0, y: shouldDropUp ? 10 : -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: shouldDropUp ? 10 : -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="rounded glass-menu shadow-lg z-[300000001]"
            style={{
              position: "fixed",
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: `${menuPosition.width}px`,
            }}
            ref={dropdownListRef}
          >
            <div className="p-2">
              <input
                type="text"
                className={cn(
                  fieldSurface(),
                  'h-8 px-3'
                )}
                placeholder={t("Search...")}
                value={searchQuery}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/[<>'"]/g, '');
                  setSearchQuery(sanitized);
                }}
              />
            </div>
            <ul className={`${popupMaxHeight ? '' : 'max-h-60'} rounded-md py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5`} style={popupMaxHeight ? { maxHeight: `${popupMaxHeight}px` } : undefined}>
              {optionsToDisplay.length > 0 ? (
                hierarchical 
                  ? renderHierarchicalOptions(optionsToDisplay)
                  : renderFlatOptions(optionsToDisplay)
              ) : (
                <li className="cursor-default select-none text-sm relative py-2 ps-3 pe-4 text-fg-muted">
                  {t("No options found")}
                </li>
              )}
              {useApi && isLoading && <li className="px-2 py-1"><Skeleton variant="option" rows={3} /></li>}
              {useApi && hasMore && !isLoading && !useLoadMoreButton && <li ref={loadMoreRef} className="h-8" />}
              {useApi && hasMore && !isLoading && useLoadMoreButton && (
                <li className="p-2">
                  <button
                    onClick={loadMoreOptions}
                    className="w-full px-3 py-2 text-xs bg-surface-sunken/70 hover:bg-surface-sunken text-fg-body rounded transition-colors"
                  >
                    {t('Load more')}
                  </button>
                </li>
              )}
            </ul>
          </motion.div>,
          document.body
        )}
      </div>
    </FieldShell>
  );
};

export default Dropdown;
